import { coreGet, coreMutate } from "./api-client";

const apiBaseUrl = import.meta.env.VITE_CRM_API_BASE_URL ?? "/api/v1";

export const GMAIL_READONLY_SCOPES = [
  "openid",
  "email",
  "https://www.googleapis.com/auth/gmail.readonly",
] as const;

export interface GmailMailbox {
  readonly id: string;
  readonly workspaceId: string;
  readonly siteId: string | null;
  readonly emailAddress: string;
  readonly provider: string;
  readonly status: string;
  readonly authorizationStatus: string;
  readonly syncStatus: string;
  readonly gmailAuthorization?: {
    readonly scopes: readonly string[];
    readonly reauthorizationEnabled: boolean;
    readonly providerEnabled?: boolean;
    readonly redirectUri?: string | null;
  };
}

export interface SyncedMail {
  id: string;
  subject: string;
  sender: { email?: string; name?: string };
  receivedAt: string | null;
  textBody?: string | null;
}

export type MailNotificationRole = "SALES" | "SOURCING";
export interface MailNotificationReceipt {
  role: MailNotificationRole;
  state: "SENDING" | "ACCEPTED" | "UNCONFIRMED";
  attemptedAt: string;
}
export interface MailNotificationStatus {
  enabled: boolean;
  targets: Record<MailNotificationRole, string>;
  ccTargets?: Record<MailNotificationRole, string | null>;
  items: MailNotificationReceipt[];
}
export function loadMailNotifications(mailboxId: string, messageId: string) {
  return coreGet<MailNotificationStatus>(
    `/mailboxes/${encodeURIComponent(mailboxId)}/messages/${encodeURIComponent(messageId)}/notifications`,
  );
}
export function sendMailNotification(
  mailboxId: string,
  messageId: string,
  role: MailNotificationRole,
) {
  return coreMutate<MailNotificationReceipt>(
    `/mailboxes/${encodeURIComponent(mailboxId)}/messages/${encodeURIComponent(messageId)}/notifications`,
    "POST",
    { role, confirmed: true },
  );
}

export function loadSyncedMail(mailboxId: string, page: number) {
  return coreGet<{
    items: SyncedMail[];
    total: number;
    page: number;
    pageSize: number;
  }>(`/mailboxes/${encodeURIComponent(mailboxId)}/messages?page=${page}`);
}

export function loadSyncedMailDetail(mailboxId: string, messageId: string) {
  return coreGet<SyncedMail>(
    `/mailboxes/${encodeURIComponent(mailboxId)}/messages/${encodeURIComponent(messageId)}`,
  );
}

export function syncGmailIncremental(mailboxId: string): Promise<unknown> {
  return coreMutate(
    `/mailboxes/${encodeURIComponent(mailboxId)}/sync/incremental`,
    "POST",
    {},
  );
}

export function syncGmailRecovery(mailboxId: string): Promise<unknown> {
  return coreMutate(
    `/mailboxes/${encodeURIComponent(mailboxId)}/sync/recovery`,
    "POST",
    {},
  );
}

interface GmailOAuthStartResult {
  readonly authorizationUrl: string;
  readonly providerMode: "GOOGLE";
  readonly scopes: readonly string[];
}

export async function loadGmailMailboxes(): Promise<GmailMailbox[]> {
  const result = await coreGet<{ readonly items: GmailMailbox[] }>(
    "/mailboxes",
  );
  return result.items.filter((mailbox) => mailbox.provider === "GMAIL");
}

export function startGmailOAuth(
  mailboxId: string,
  scopes: readonly string[],
  redirectUri?: string,
  password?: string,
): Promise<GmailOAuthStartResult> {
  return coreMutate<GmailOAuthStartResult>("/gmail/oauth/start", "POST", {
    mailboxId,
    redirectUri:
      redirectUri ??
      new URL(
        `${apiBaseUrl.replace(/\/$/, "")}/gmail/oauth/callback`,
        window.location.origin,
      ).href,
    scopes,
    state: `crm-gmail-${crypto.randomUUID()}`,
    ...(password ? { password } : {}),
  });
}

export function revokeGmailAccess(
  mailboxId: string,
  password?: string,
): Promise<{ googleRevoked: boolean }> {
  return coreMutate(
    `/gmail/oauth/${encodeURIComponent(mailboxId)}/revoke`,
    "POST",
    {
      reason: "Administrator revoked Gmail connection",
      ...(password ? { password } : {}),
    },
  );
}
